import React from "react";

import { Route, withRouter, Switch } from "react-router-dom";
import asyncComponent from "../../helpers/AsyncFunc";
import PermissionWrapper from "../../components/PermissionWrapper";
import { satisfyPermissionSets } from "../../hooks/permission";
import { usePermissions } from "../../query/api";

const routes = [
  {
    path: "",
    component: asyncComponent(() => import("../Member/index.js")),
    permissionSets: [[{ section: "MEMBER", function: "LIST" }]],
  },
  {
    path: "banner",
    component: asyncComponent(() => import("../Banner/index.js")),
    permissionSets: [[{ section: "BANNER", function: "LIST" }]],
  },
  {
    path: "banner/create",
    component: asyncComponent(() => import("../Banner/create.js")),
    permissionSets: [[{ section: "BANNER", function: "CREATE" }]],
  },
  {
    path: "banner/edit/:id",
    component: asyncComponent(() => import("../Banner/edit.js")),
    permissionSets: [[{ section: "BANNER", function: "EDIT" }]],
  },
  {
    path: "banner/view/:id",
    component: asyncComponent(() => import("../Banner/view.js")),
    permissionSets: [[{ section: "BANNER", function: "VIEW_DETAILS" }]],
  },
  {
    path: "dynamicBannerList",
    component: asyncComponent(() => import("../DynamicBannerList/index.js")),
    permissionSets: [[{ section: "BANNER_LIST", function: "LIST" }]],
  },
  {
    path: "dynamicBannerList/edit/:id",
    component: asyncComponent(() => import("../DynamicBannerList/edit.js")),
    permissionSets: [[{ section: "BANNER_LIST", function: "EDIT" }]],
  },
  {
    path: "dynamicBannerList/view/:id",
    component: asyncComponent(() => import("../DynamicBannerList/view.js")),
    permissionSets: [[{ section: "BANNER_LIST", function: "VIEW_DETAILS" }]],
  },
  {
    path: "dynamicHub",
    component: asyncComponent(() => import("../DynamicHub/index.js")),
    // CTA permissions have not been returned by the authority API yet.
    // Keep the route visible until the backend permission configuration lands.
    permissionSets: [[]],
  },
  {
    path: "dynamicHub/create",
    component: asyncComponent(() => import("../DynamicHub/create.js")),
    permissionSets: [[]],
  },
  {
    path: "dynamicHub/edit/:id",
    component: asyncComponent(() => import("../DynamicHub/edit.js")),
    permissionSets: [[]],
  },
  {
    path: "dashboard",
    component: asyncComponent(() => import("../Member/index.js")),
    permissionSets: [[{ section: "MEMBER", function: "LIST" }]],
  },
  {
    path: "contactlessParking",
    component: asyncComponent(() => import("../ContactlessParking/index.js")),
    permissionSets: [[{ section: "CONTACTLESS_PARKING", function: "LIST" }]],
  },
  {
    path: "contactlessParking/edit/:id",
    component: asyncComponent(() => import("../ContactlessParking/edit.js")),
    permissionSets: [
      [
        {
          section: "CONTACTLESS_PARKING",
          function: "VIEW_PARKING_RECORDS",
        },
      ],
      [
        {
          section: "CONTACTLESS_PARKING",
          function: "EXIT_CARPARK",
        },
      ],
      [
        {
          section: "CONTACTLESS_PARKING",
          function: "SETTLE_PAYMENT",
        },
      ],
    ],
  },
  {
    path: "parkingRecord",
    component: asyncComponent(() => import("../ParkingRecord/index.js")),
    permissionSets: [[{ section: "PARKING_RECORDS", function: "LIST" }]],
  },
  {
    path: "parkingRecord/create",
    component: asyncComponent(() => import("../ParkingRecord/create.js")),
    permissionSets: [[{ section: "PARKING_RECORDS", function: "CREATE" }]],
  },
  {
    path: "member",
    component: asyncComponent(() => import("../Member/index.js")),
    permissionSets: [[{ section: "MEMBER", function: "LIST" }]],
  },
  {
    path: "member/view/:userId",
    component: asyncComponent(() => import("../Member/view.js")),
    permissionSets: [[{ section: "MEMBER", function: "VIEW_DETAILS" }]],
  },
  {
    path: "member/edit/:userId",
    component: asyncComponent(() => import("../Member/edit.js")),
    permissionSets: [
      [
        { section: "MEMBER", function: "VIEW_DETAILS" },
        { section: "MEMBER", function: "EDIT" },
      ],
    ],
  },
  {
    path: "member/create",
    component: asyncComponent(() => import("../Member/create.js")),
    permissionSets: [[{ section: "MEMBER", function: "CREATE" }]],
  },
  {
    path: "spendingTransaction",
    component: asyncComponent(() => import("../SpendingTransaction/index.js")),
    permissionSets: [[{ section: "SPENDING_TRANSACTION", function: "LIST" }]],
  },
  {
    path: "gift",
    component: asyncComponent(() => import("../Gift/index.js")),
    permissionSets: [[{ section: "GIFT", function: "LIST" }]],
  },
  {
    path: "gift/create",
    component: asyncComponent(() => import("../Gift/create.js")),
    permissionSets: [[{ section: "GIFT", function: "CREATE" }]],
  },
  {
    path: "gift/edit/:id",
    component: asyncComponent(() => import("../Gift/edit.js")),
    permissionSets: [[{ section: "GIFT", function: "EDIT" }]],
  },
  {
    path: "gift/view/:id",
    component: asyncComponent(() => import("../Gift/view.js")),
    permissionSets: [[{ section: "GIFT", function: "VIEW_DETAILS" }]],
  },
  {
    path: "receiptScanning",
    component: asyncComponent(() => import("../ReceiptScanning/index.js")),
    permissionSets: [],
  },
  {
    path: "onlineEnquiry",
    component: asyncComponent(() => import("../OnlineEnquiry/index.js")),
    permissionSets: [],
  },
  {
    path: "onlineEnquiry/edit/:id",
    component: asyncComponent(() => import("../OnlineEnquiry/edit.js")),
    permissionSets: [],
  },
  {
    path: "sdollar",
    component: asyncComponent(() => import("../SDollar/index.js")),
    permissionSets: [
      [{ section: "S_COUPON_AND_S_DOLLAR_SHOPS", function: "LIST" }],
    ],
  },
  {
    path: "sdollar/view/:id",
    component: asyncComponent(() => import("../SDollar/view.js")),
    permissionSets: [
      [{ section: "S_COUPON_AND_S_DOLLAR_SHOPS", function: "VIEW_DETAILS" }],
    ],
  },
  {
    path: "mall",
    component: asyncComponent(() => import("../Mall/index.js")),
    permissionSets: [[{ section: "SHOP", function: "LIST" }]],
  },
  {
    path: "mall/edit/:id",
    component: asyncComponent(() => import("../Mall/edit.js")),
    permissionSets: [[{ section: "SHOP", function: "EDIT" }]],
  },
  {
    path: "shop",
    component: asyncComponent(() => import("../Shop/index.js")),
    permissionSets: [[{ section: "SHOP", function: "LIST" }]],
  },
  // {
  //   path: "shop/create",
  //   component: asyncComponent(() => import("../Shop/create.js")),
  //   permissionSets: [[{ section: "SHOP", function: "CREATE" }]],
  // },
  {
    path: "shop/view/:id",
    component: asyncComponent(() => import("../Shop/view.js")),
    permissionSets: [[{ section: "SHOP", function: "VIEW_DETAILS" }]],
  },
  {
    path: "crazyAd",
    component: asyncComponent(() => import("../CrazyAd/index.js")),
    permissionSets: [[{ section: "CRAZY_AD", function: "LIST" }]],
  },
  {
    path: "crazyAd/create",
    component: asyncComponent(() => import("../CrazyAd/create.js")),
    permissionSets: [[{ section: "CRAZY_AD", function: "CREATE" }]],
  },
  {
    path: "crazyAd/edit/:id",
    component: asyncComponent(() => import("../CrazyAd/edit.js")),
    permissionSets: [[{ section: "CRAZY_AD", function: "EDIT" }]],
  },
  {
    path: "crazyAd/view/:id",
    component: asyncComponent(() => import("../CrazyAd/view.js")),
    permissionSets: [[{ section: "CRAZY_AD", function: "VIEW_DETAILS" }]],
  },
  {
    path: "mission",
    component: asyncComponent(() => import("../Activity/Mission/index.js")),
    requiredPermissionSets: [[{ section: "ACTIVITY", function: "LIST" }]],
    permissionSets: [[{ section: "ACTIVITY", function: "LIST" }]],
  },
  {
    path: "mission/create",
    component: asyncComponent(() => import("../Activity/Mission/create.js")),
    permissionSets: [[{ section: "ACTIVITY", function: "CREATE" }]],
  },
  {
    path: "mission/edit/:id",
    component: asyncComponent(() => import("../Activity/Mission/edit.js")),
    permissionSets: [[{ section: "ACTIVITY", function: "EDIT" }]],
  },
  {
    path: "mission/view/:id",
    component: asyncComponent(() => import("../Activity/Mission/view.js")),
    permissionSets: [[{ section: "ACTIVITY", function: "VIEW_DETAILS" }]],
  },
  {
    path: "mission/edit/:missionId/memberSegment/create",
    component: asyncComponent(
      () => import("../Activity/MemberSegment/MissionSegment/create.js"),
    ),
    permissionSets: [
      [{ section: "ACTIVITY", function: "CREATE" }],
      [{ section: "ACTIVITY", function: "EDIT" }],
    ],
  },
  {
    path: "mission/edit/:missionId/memberSegment/edit/:id",
    component: asyncComponent(
      () => import("../Activity/MemberSegment/MissionSegment/edit.js"),
    ),
    permissionSets: [
      [{ section: "ACTIVITY", function: "CREATE" }],
      [{ section: "ACTIVITY", function: "EDIT" }],
    ],
  },
  {
    path: "mission/view/:missionId/memberSegment/view/:id",
    component: asyncComponent(
      () => import("../Activity/MemberSegment/MissionSegment/view.js"),
    ),
    permissionSets: [[{ section: "ACTIVITY", function: "VIEW_DETAILS" }]],
  },
  {
    path: "stamp",
    component: asyncComponent(() => import("../Activity/Stamp/index.js")),
    permissionSets: [[{ section: "ACTIVITY", function: "LIST" }]],
  },
  {
    path: "stamp/create",
    component: asyncComponent(() => import("../Activity/Stamp/create.js")),
    permissionSets: [[{ section: "ACTIVITY", function: "CREATE" }]],
  },
  {
    path: "stamp/edit/:id",
    component: asyncComponent(() => import("../Activity/Stamp/edit.js")),
    permissionSets: [[{ section: "ACTIVITY", function: "EDIT" }]],
  },
  {
    path: "stamp/view/:id",
    component: asyncComponent(() => import("../Activity/Stamp/view.js")),
    permissionSets: [[{ section: "ACTIVITY", function: "VIEW_DETAILS" }]],
  },
  {
    path: "stamp/edit/:stampId/memberSegment/create",
    component: asyncComponent(
      () => import("../Activity/MemberSegment/StampSegment/create.js"),
    ),
    permissionSets: [
      [{ section: "ACTIVITY", function: "CREATE" }],
      [{ section: "ACTIVITY", function: "EDIT" }],
    ],
  },
  {
    path: "stamp/edit/:stampId/memberSegment/edit/:id",
    component: asyncComponent(
      () => import("../Activity/MemberSegment/StampSegment/edit.js"),
    ),
    permissionSets: [
      [{ section: "ACTIVITY", function: "CREATE" }],
      [{ section: "ACTIVITY", function: "EDIT" }],
    ],
  },
  {
    path: "stamp/view/:stampId/memberSegment/view/:id",
    component: asyncComponent(
      () => import("../Activity/MemberSegment/StampSegment/view.js"),
    ),
    permissionSets: [[{ section: "ACTIVITY", function: "VIEW_DETAILS" }]],
  },
  // deprecated, backwards compatibility
  {
    path: "campaign",
    component: asyncComponent(() => import("../Campaign/index.js")),
    permissionSets: [[{ section: "CAMPAIGN", function: "LIST" }]],
  },
  {
    path: "campaign/create",
    component: asyncComponent(() => import("../Campaign/create.js")),
    permissionSets: [[{ section: "CAMPAIGN", function: "CREATE" }]],
  },
  {
    path: "campaign/edit/:id",
    component: asyncComponent(() => import("../Campaign/edit.js")),
    permissionSets: [[{ section: "CAMPAIGN", function: "EDIT" }]],
  },
  {
    path: "coupon",
    component: asyncComponent(() => import("../Coupon/index.js")),
    permissionSets: [[{ section: "S_COUPON", function: "LIST" }]],
  },
  {
    path: "coupon/create",
    component: asyncComponent(() => import("../Coupon/create.js")),
    permissionSets: [[{ section: "S_COUPON", function: "CREATE" }]],
  },
  {
    path: "coupon/edit/:id",
    component: asyncComponent(() => import("../Coupon/edit.js")),
    permissionSets: [[{ section: "S_COUPON", function: "EDIT" }]],
  },
  {
    path: "coupon/view/:id",
    component: asyncComponent(() => import("../Coupon/view.js")),
    permissionSets: [[{ section: "S_COUPON", function: "VIEW_DETAILS" }]],
  },
  // deprecated, backwards compatibility
  // {
  //   path: "promotion",
  //   component: asyncComponent(() => import("../Promotion/index.js")),
  //   permissionSets: [[{ section: "BANNER", function: "LIST" }]],
  // },
  // {
  //   path: "promotion/create",
  //   component: asyncComponent(() => import("../Promotion/create.js")),
  //   permissionSets: [[{ section: "BANNER", function: "CREATE" }]],
  // },
  // {
  //   path: "promotion/edit/:id",
  //   component: asyncComponent(() => import("../Promotion/edit.js")),
  //   permissionSets: [[{ section: "BANNER", function: "EDIT" }]],
  // },
  //
  {
    path: "unpublishedPrivilege",
    component: asyncComponent(() => import("../UnpublishedPrivilege/index.js")),
    permissionSets: [[{ section: "EXCLUSIVE_REWARD", function: "LIST" }]],
  },
  {
    path: "unpublishedPrivilege/create",
    component: asyncComponent(
      () => import("../UnpublishedPrivilege/create.js"),
    ),
    permissionSets: [[{ section: "EXCLUSIVE_REWARD", function: "CREATE" }]],
  },
  {
    path: "unpublishedPrivilege/edit/:id",
    component: asyncComponent(() => import("../UnpublishedPrivilege/edit.js")),
    permissionSets: [[{ section: "EXCLUSIVE_REWARD", function: "EDIT" }]],
  },
  {
    path: "unpublishedPrivilege/view/:id",
    component: asyncComponent(() => import("../UnpublishedPrivilege/view.js")),
    permissionSets: [
      [{ section: "EXCLUSIVE_REWARD", function: "VIEW_DETAILS" }],
    ],
  },
  // {
  //   path: "badge",
  //   component: asyncComponent(() => import("../Badge/index.js")),
  //   permissionSets: [[{ section: "BADGE", function: "LIST" }]],
  // },
  // {
  //   path: "badge/create",
  //   component: asyncComponent(() => import("../Badge/create.js")),
  //   permissionSets: [[{ section: "BADGE", function: "CREATE" }]],
  // },
  // {
  //   path: "badge/edit/:id",
  //   component: asyncComponent(() => import("../Badge/edit.js")),
  //   permissionSets: [[{ section: "BADGE", function: "EDIT" }]],
  // },
  // {
  //   path: "badge/editSp/:id",
  //   component: asyncComponent(() => import("../Badge/editSp.js")),
  //   permissionSets: [[{ section: "MISSION", function: "EDIT" }]],
  // },
  // {
  //   path: "badgeOfferPromotion",
  //   component: asyncComponent(() => import("../BadgeOfferPromotion/index.js")),
  //   permissionSets: [[{ section: "EXCLUSIVE_REWARD", function: "LIST" }]],
  // },
  // {
  //   path: "badgeOfferPromotion/create",
  //   component: asyncComponent(() => import("../BadgeOfferPromotion/create.js")),
  //   permissionSets: [[{ section: "EXCLUSIVE_REWARD", function: "CREATE" }]],
  // },
  // {
  //   path: "badgeOfferPromotion/edit/:id",
  //   component: asyncComponent(() => import("../BadgeOfferPromotion/edit.js")),
  //   permissionSets: [[{ section: "EXCLUSIVE_REWARD", function: "EDIT" }]],
  // },
  {
    path: "memberTags",
    component: asyncComponent(() => import("../MemberTags/index.js")),
    permissionSets: [[{ section: "MEMBER_TAG", function: "LIST" }]],
  },
  {
    path: "memberTags/create",
    component: asyncComponent(() => import("../MemberTags/create.js")),
    permissionSets: [[{ section: "MEMBER_TAG", function: "CREATE" }]],
  },
  {
    path: "memberTags/edit/:id",
    component: asyncComponent(() => import("../MemberTags/edit.js")),
    permissionSets: [[{ section: "MEMBER_TAG", function: "EDIT" }]],
  },
  {
    path: "memberTags/view/:id",
    component: asyncComponent(() => import("../MemberTags/view.js")),
    permissionSets: [[{ section: "MEMBER_TAG", function: "VIEW_DETAILS" }]],
  },
  {
    path: "spendingPointBonusRule",
    component: asyncComponent(
      () => import("../SpendingPointBonusRule/index.js"),
    ),
    permissionSets: [[{ section: "BONUS_RULE", function: "LIST" }]],
  },
  {
    path: "spendingPointBonusRule/create",
    component: asyncComponent(
      () => import("../SpendingPointBonusRule/create.js"),
    ),
    permissionSets: [[{ section: "BONUS_RULE", function: "CREATE" }]],
  },
  {
    path: "spendingPointBonusRule/edit/:id",
    component: asyncComponent(
      () => import("../SpendingPointBonusRule/edit.js"),
    ),
    permissionSets: [[{ section: "BONUS_RULE", function: "EDIT" }]],
  },
  {
    path: "spendingPointBonusRule/view/:id",
    component: asyncComponent(
      () => import("../SpendingPointBonusRule/view.js"),
    ),
    permissionSets: [[{ section: "BONUS_RULE", function: "VIEW_DETAILS" }]],
  },
  {
    path: "user",
    component: asyncComponent(() => import("../UserManagement/index.js")),
    permissionSets: [[{ section: "USER", function: "LIST" }]],
  },
  {
    path: "user/create",
    component: asyncComponent(() => import("../UserManagement/create.js")),
    permissionSets: [[{ section: "USER", function: "CREATE" }]],
  },
  {
    path: "user/edit/:id",
    component: asyncComponent(() => import("../UserManagement/create.js")),
    permissionSets: [[{ section: "USER", function: "EDIT" }]],
  },
  {
    path: "userNotification",
    component: asyncComponent(() => import("../UserNotification/index.js")),
    permissionSets: [[{ section: "USER_PUSH_RECORD", function: "LIST" }]],
  },
  {
    path: "userNotification/create",
    component: asyncComponent(() => import("../UserNotification/create.js")),
    permissionSets: [[{ section: "USER_PUSH_RECORD", function: "CREATE" }]],
  },
  {
    path: "userNotification/edit/:id",
    component: asyncComponent(() => import("../UserNotification/edit.js")),
    permissionSets: [[{ section: "USER_PUSH_RECORD", function: "EDIT" }]],
  },
  {
    path: "userNotification/view/:id",
    component: asyncComponent(() => import("../UserNotification/view.js")),
    permissionSets: [
      [{ section: "USER_PUSH_RECORD", function: "VIEW_DETAILS" }],
    ],
  },
  {
    path: "merchantNotification",
    component: asyncComponent(() => import("../MerchantNotification/index.js")),
    permissionSets: [[{ section: "MERCHANT_PUSH_RECORD", function: "LIST" }]],
  },
  {
    path: "merchantNotification/create",
    component: asyncComponent(
      () => import("../MerchantNotification/create.js"),
    ),
    permissionSets: [[{ section: "MERCHANT_PUSH_RECORD", function: "CREATE" }]],
  },
  {
    path: "merchantNotification/edit/:id",
    component: asyncComponent(() => import("../MerchantNotification/edit.js")),
    permissionSets: [[{ section: "MERCHANT_PUSH_RECORD", function: "EDIT" }]],
  },
  {
    path: "userInboxMessage",
    component: asyncComponent(() => import("../UserInboxMessage/index.js")),
    permissionSets: [[{ section: "USER_INBOX_MESSAGE", function: "LIST" }]],
  },
  {
    path: "userInboxMessage/create",
    component: asyncComponent(() => import("../UserInboxMessage/create.js")),
    permissionSets: [[{ section: "USER_INBOX_MESSAGE", function: "CREATE" }]],
  },
  {
    path: "userInboxMessage/edit/:id",
    component: asyncComponent(() => import("../UserInboxMessage/edit.js")),
    permissionSets: [[{ section: "USER_INBOX_MESSAGE", function: "EDIT" }]],
  },
  {
    path: "userInboxMessage/view/:id",
    component: asyncComponent(() => import("../UserInboxMessage/view.js")),
    permissionSets: [
      [{ section: "USER_INBOX_MESSAGE", function: "VIEW_DETAILS" }],
    ],
  },
  {
    path: "merchantInboxMessage",
    component: asyncComponent(() => import("../MerchantInboxMessage/index.js")),
    permissionSets: [[{ section: "MERCHANT_INBOX_MESSAGE", function: "LIST" }]],
  },
  {
    path: "merchantInboxMessage/create",
    component: asyncComponent(
      () => import("../MerchantInboxMessage/create.js"),
    ),
    permissionSets: [
      [{ section: "MERCHANT_INBOX_MESSAGE", function: "CREATE" }],
    ],
  },
  {
    path: "merchantInboxMessage/edit/:id",
    component: asyncComponent(() => import("../MerchantInboxMessage/edit.js")),
    permissionSets: [[{ section: "MERCHANT_INBOX_MESSAGE", function: "EDIT" }]],
  },
  {
    path: "merchantInboxMessage/view/:id",
    component: asyncComponent(() => import("../MerchantInboxMessage/view.js")),
    permissionSets: [
      [{ section: "MERCHANT_INBOX_MESSAGE", function: "VIEW_DETAILS" }],
    ],
  },
  {
    path: "changePassword",
    component: asyncComponent(() => import("../ChangePassword/index.js")),
    permissionSets: [[{ section: "SHOP", function: "CHANGE_PASSWORD" }]],
  },
  {
    path: "checkInEvent",
    component: asyncComponent(() => import("../CheckInEvent/index.js")),
    permissionSets: [[{ section: "CHECK_IN_EVENT", function: "LIST" }]],
  },
  {
    path: "checkInEvent/create",
    component: asyncComponent(() => import("../CheckInEvent/create.js")),
    permissionSets: [[{ section: "CHECK_IN_EVENT", function: "CREATE" }]],
  },
  {
    path: "checkInEvent/edit/:id",
    component: asyncComponent(() => import("../CheckInEvent/edit.js")),
    permissionSets: [[{ section: "CHECK_IN_EVENT", function: "EDIT" }]],
  },
  {
    path: "checkInEvent/view/:id",
    component: asyncComponent(() => import("../CheckInEvent/view.js")),
    permissionSets: [[{ section: "CHECK_IN_EVENT", function: "VIEW_DETAILS" }]],
  },
  {
    path: "merchantCustomReferralPoint",
    component: asyncComponent(
      () => import("../MerchantCustomReferralPoint/index.js"),
    ),
    permissionSets: [
      [{ section: "CUSTOM_REFERRAL_BONUS_POINT", function: "LIST" }],
    ],
  },
  {
    path: "merchantCustomReferralPoint/create",
    component: asyncComponent(
      () => import("../MerchantCustomReferralPoint/create.js"),
    ),
    permissionSets: [
      [{ section: "CUSTOM_REFERRAL_BONUS_POINT", function: "CREATE" }],
    ],
  },
  {
    path: "merchantCustomReferralPoint/edit/:id",
    component: asyncComponent(
      () => import("../MerchantCustomReferralPoint/edit.js"),
    ),
    permissionSets: [
      [{ section: "CUSTOM_REFERRAL_BONUS_POINT", function: "EDIT" }],
    ],
  },
  {
    path: "thirdPartyIntegrator",
    component: asyncComponent(() => import("../ThirdPartyIntegrator/index.js")),
    permissionSets: [[{ section: "THIRD_PARTY_INTEGRATOR", function: "LIST" }]],
  },
  {
    path: "thirdPartyIntegrator/create",
    component: asyncComponent(
      () => import("../ThirdPartyIntegrator/create.js"),
    ),
    permissionSets: [
      [{ section: "THIRD_PARTY_INTEGRATOR", function: "CREATE" }],
    ],
  },
  {
    path: "thirdPartyIntegrator/edit/:id",
    component: asyncComponent(() => import("../ThirdPartyIntegrator/edit.js")),
    permissionSets: [[{ section: "THIRD_PARTY_INTEGRATOR", function: "EDIT" }]],
  },
  {
    path: "thirdPartyIntegrator/view/:id",
    component: asyncComponent(() => import("../ThirdPartyIntegrator/view.js")),
    permissionSets: [
      [{ section: "THIRD_PARTY_INTEGRATOR", function: "VIEW_DETAILS" }],
    ],
  },
  // Sino Club Member
  {
    path: "sinoClubMember",
    component: asyncComponent(() => import("../SinoClubMember/index.js")),
    permissionSets: [[{ section: "SINO_CLUB", function: "LIST_MEMBERS" }]],
  },
  {
    path: "sinoClubMember/view/:id",
    component: asyncComponent(() => import("../SinoClubMember/view.js")),
    permissionSets: [
      [{ section: "SINO_CLUB", function: "VIEW_MEMBER_DETAILS" }],
    ],
  },
  {
    path: "sinoClubMember/edit/:id",
    component: asyncComponent(() => import("../SinoClubMember/edit.js")),
    permissionSets: [[{ section: "SINO_CLUB", function: "EDIT_MEMBER" }]],
  },
  {
    path: "sinoClubMember/create",
    component: asyncComponent(() => import("../SinoClubMember/create.js")),
    permissionSets: [[{ section: "SINO_CLUB", function: "CREATE_MEMBER" }]],
  },
  {
    path: "sinoClubMember/import",
    component: asyncComponent(() => import("../SinoClubMember/import.js")),
    permissionSets: [[{ section: "SINO_CLUB", function: "IMPORT_MEMBER" }]],
  },
  {
    path: "sinoClubMembershipUpgrade",
    component: asyncComponent(
      () => import("../SinoClubMembershipUpgrade/index.js"),
    ),
    permissionSets: [
      [{ section: "SINO_CLUB", function: "LIST_MEMBERSHIP_UPGRADE" }],
    ],
  },
  {
    path: "sinoParking",
    component: asyncComponent(() => import("../SinoParking/index.js")),
    permissionSets: [[{ section: "SINO_PARKING", function: "LIST" }]],
  },
  {
    path: "estate",
    component: asyncComponent(() => import("../Estate/index.js")),
    permissionSets: [[{ section: "ESTATE", function: "LIST" }]],
  },
  {
    path: "estate/edit/:id",
    component: asyncComponent(() => import("../Estate/edit.js")),
    permissionSets: [[{ section: "ESTATE", function: "EDIT" }]],
  },
  {
    path: "estate/view/:id",
    component: asyncComponent(() => import("../Estate/view.js")),
    permissionSets: [[{ section: "ESTATE", function: "VIEW_DETAILS" }]],
  },
  {
    path: "estate/create",
    component: asyncComponent(() => import("../Estate/create.js")),
    permissionSets: [[{ section: "ESTATE", function: "CREATE" }]],
  },
  {
    path: "sinoClubPMemberReferralCode",
    component: asyncComponent(
      () => import("../SinoClubPMemberReferralCode/index.js"),
    ),
    permissionSets: [
      [{ section: "SINO_CLUB_PRIVILEGE_MEMBER_REFERRAL", function: "LIST" }],
    ],
  },
  {
    path: "sinoClubPMemberReferralCode/create",
    component: asyncComponent(
      () => import("../SinoClubPMemberReferralCode/create.js"),
    ),
    permissionSets: [
      [{ section: "SINO_CLUB_PRIVILEGE_MEMBER_REFERRAL", function: "CREATE" }],
    ],
  },
  {
    path: "sinoClubPMemberReferralCode/edit/:id",
    component: asyncComponent(
      () => import("../SinoClubPMemberReferralCode/edit.js"),
    ),
    permissionSets: [
      [{ section: "SINO_CLUB_PRIVILEGE_MEMBER_REFERRAL", function: "EDIT" }],
    ],
  },
  {
    path: "sinoClubPMemberReferralCode/view/:id",
    component: asyncComponent(
      () => import("../SinoClubPMemberReferralCode/view.js"),
    ),
    permissionSets: [
      [
        {
          section: "SINO_CLUB_PRIVILEGE_MEMBER_REFERRAL",
          function: "VIEW_DETAILS",
        },
      ],
    ],
  },
  {
    path: "campaignCode",
    component: asyncComponent(() => import("../CampaignCode/index.js")),
    permissionSets: [[{ section: "CAMPAIGN_CODE", function: "LIST" }]],
  },
  {
    path: "campaignCode/create",
    component: asyncComponent(() => import("../CampaignCode/create.js")),
    permissionSets: [[{ section: "CAMPAIGN_CODE", function: "CREATE" }]],
  },
  {
    path: "campaignCode/edit/:campaignId",
    component: asyncComponent(() => import("../CampaignCode/create.js")),
    permissionSets: [[{ section: "CAMPAIGN_CODE", function: "EDIT" }]],
  },
  {
    path: "campaignCode/view/:campaignId",
    component: asyncComponent(() => import("../CampaignCode/view.js")),
    permissionSets: [[{ section: "CAMPAIGN_CODE", function: "VIEW_DETAILS" }]],
  },
];

const AppRouter = ({ style }) => {
  const { data } = usePermissions();
  return (
    <div style={style}>
      <Switch>
        <Route
          path="/"
          exact
          component={asyncComponent(() => import("../Root/index"))}
        />
        <Route
          exact
          path="/changePassword"
          component={asyncComponent(() => import("../ChangePassword/index.js"))}
        />
        {routes
          .filter(({ permissionSets, requireSuperPlusAdmin }) =>
            satisfyPermissionSets(
              data?.data,
              permissionSets,
              requireSuperPlusAdmin,
            ),
          )
          .map((singleRoute) => {
            const { path, exact, ...otherProps } = singleRoute;
            return (
              <Route
                exact={exact ?? true}
                key={path}
                path={`/${path}`}
                {...otherProps}
              />
            );
          })}
      </Switch>
    </div>
  );
};

export default withRouter(AppRouter);
